import { z } from 'zod';
export const formFieldTypeSchema = z.enum([
    'text', 'textarea', 'number', 'date', 'boolean', 'select', 'multiselect', 'scale',
]);
export const FORM_FIELD_TYPE_LABELS = {
    text: 'Teks singkat',
    textarea: 'Teks panjang',
    number: 'Angka',
    date: 'Tanggal',
    boolean: 'Ya / Tidak',
    select: 'Pilihan tunggal',
    multiselect: 'Pilihan ganda',
    scale: 'Skala',
};
export const formFieldInputSchema = z.object({
    /** Stable key for cross-version reporting. Labels may change; keys should not. */
    key: z.string().trim().regex(/^[a-z][a-z0-9_]{0,60}$/, 'Key harus huruf kecil, angka, dan garis bawah; dimulai huruf'),
    label: z.string().trim().min(1, 'Label wajib diisi').max(300),
    type: formFieldTypeSchema,
    required: z.boolean().default(false),
    helpText: z.string().trim().max(500).optional().nullable(),
    options: z.array(z.object({
        value: z.string().trim().min(1).max(100),
        label: z.string().trim().min(1).max(200),
    })).optional().nullable(),
    minValue: z.coerce.number().optional().nullable(),
    maxValue: z.coerce.number().optional().nullable(),
    sortOrder: z.coerce.number().int().min(0).max(999).default(0),
}).superRefine((f, ctx) => {
    const butuhOpsi = f.type === 'select' || f.type === 'multiselect';
    if (butuhOpsi && (!f.options || f.options.length === 0)) {
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'Tipe pilihan wajib punya minimal satu opsi.' });
    }
    if (!butuhOpsi && f.options && f.options.length > 0) {
        ctx.addIssue({ code: 'custom', path: ['options'], message: 'Tipe ini tidak memakai daftar opsi.' });
    }
    if (f.type === 'scale' && (f.minValue == null || f.maxValue == null)) {
        ctx.addIssue({ code: 'custom', path: ['minValue'], message: 'Skala wajib punya nilai minimum dan maksimum.' });
    }
    if (f.minValue != null && f.maxValue != null && f.minValue >= f.maxValue) {
        ctx.addIssue({ code: 'custom', path: ['maxValue'], message: 'Maksimum harus lebih besar dari minimum.' });
    }
});
/** What a form is for. A services admin reaches only `service` forms. */
export const formPurposeSchema = z.enum(['donor', 'service']);
export const formTemplateInputSchema = z.object({
    name: z.string().trim().min(2, 'Nama form minimal 2 karakter').max(200),
    description: z.string().trim().max(1000).optional().nullable(),
    branchId: z.string().uuid().optional().nullable(),
    /** Omitted means `donor` — unless the author may only write service forms. */
    purpose: formPurposeSchema.optional(),
    fields: z.array(formFieldInputSchema).min(1, 'Form harus punya minimal satu pertanyaan'),
}).superRefine((v, ctx) => {
    const keys = v.fields.map((f) => f.key);
    const kembar = keys.filter((k, i) => keys.indexOf(k) !== i);
    if (kembar.length > 0) {
        ctx.addIssue({ code: 'custom', path: ['fields'], message: `Key duplikat: ${[...new Set(kembar)].join(', ')}` });
    }
});
export const formVersionInputSchema = z.object({
    fields: z.array(formFieldInputSchema).min(1),
    note: z.string().trim().max(500).optional().nullable(),
});
/** Answers travel as a key -> value map. The server validates them against the field definitions. */
export const formAnswersSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]));
/** A version whose fields are locked can only be superseded, never edited. */
export const isVersionLocked = (v) => v.submissionCount > 0;
